// Üzenet-melléklet szekció — a KÖZÖS `components/shared/attachment-panel.tsx` üzenet-oldali bekötése
// (a megjelenítés és a viselkedés ott él; a jegy-csatolmány ugyanazt használja). Itt csak az üzenet-specifikus
// rész van: a lista-query, a KÉT végpont-hívás, a cache-invalidálás és a SZÖVEGEK.
//
// A tenant-kérdés (miért a belső MORGENS ügyfél-rekord a fájl gazdája) a BFF-oldalon dől el; a UI annyit tud,
// hogy a szerver 503-at ad, ha nincs konfigurálva — arra saját üzenet van (`attachmentUnavailable`).
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  AttachmentPanel,
  type AttachmentDeleteOutcome,
} from '@/components/shared/attachment-panel';
import { messageKeys } from '@/features/messages/api';
import { MAX_ATTACHMENTS, uploadMessageAttachment } from '@/features/messages/attachment-upload';
import { api } from '@/lib/api';

export interface MessageAttachmentsProps {
  messageId: string;
  /** A „Mellékletek N" cím elhagyása (a hordozó felület már megnevezte a blokkot) — lásd `AttachmentPanel`. */
  hideHeader?: boolean;
  /** Feltöltés/törlés joga (messages.manage) — enélkül csak olvasható a lista. */
  canManage: boolean;
  /** A panel csak akkor kérdez, ha a hordozó felület (sheet) nyitva van. */
  enabled: boolean;
  className?: string;
}

export function MessageAttachments({
  messageId,
  canManage,
  enabled,
  className,
  hideHeader = false,
}: MessageAttachmentsProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const queryClient = useQueryClient();

  const listKey = messageKeys.attachments(messageId);
  const listQuery = api.messageAttachments.list.useQuery(
    listKey,
    { params: { id: messageId } },
    { queryKey: listKey, enabled: enabled && messageId !== '' },
  );
  const rows = listQuery.data?.status === 200 ? listQuery.data.body : [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: listKey });
    // A LISTA melléklet-számlálója (`attachmentCount`) is elavul → a lista-cache is invalidálódik.
    void queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
  };

  const onDelete = async (attachmentId: string): Promise<AttachmentDeleteOutcome> => {
    const res = await api.messageAttachments.delete.mutation({
      params: { id: messageId, attachmentId },
    });
    if (res.status === 200) return 'ok';
    if (res.status === 403) return 'forbidden';
    if (res.status === 503) return 'unavailable';
    return 'failed';
  };

  return (
    <AttachmentPanel
      hideHeader={hideHeader}
      rows={rows}
      loading={listQuery.isLoading}
      canManage={canManage}
      onUpload={(file) => uploadMessageAttachment(messageId, file)}
      onDelete={onDelete}
      onChanged={invalidate}
      formatDate={(iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale);
      }}
      {...(className === undefined ? {} : { className })}
      labels={{
        title: t('messages.attachments.title'),
        upload: t('messages.attachments.upload'),
        uploading: t('messages.attachments.uploading'),
        empty: t('messages.attachments.empty'),
        download: t('messages.attachments.download'),
        remove: t('messages.attachments.delete'),
        removing: t('messages.attachments.deleting'),
        removeTitle: t('messages.attachments.deleteTitle'),
        removeDescription: (name) => t('messages.attachments.deleteDesc', { name }),
        cancel: t('messages.cancel'),
        unavailable: t(`messages.attachments.unavailable`),
        limitReached: t('messages.attachments.limitReached', { max: MAX_ATTACHMENTS }),
      }}
      messages={{
        limit: t('toast:messages.attachmentLimit', { max: MAX_ATTACHMENTS }),
        invalidType: t('toast:messages.attachmentInvalidType'),
        tooLarge: t('toast:messages.attachmentTooLarge'),
        uploaded: t('toast:messages.attachmentUploaded'),
        uploadFailed: t('toast:messages.attachmentError'),
        forbidden: t('toast:messages.forbidden'),
        validation: t('toast:messages.attachmentValidation'),
        unavailable: t('toast:messages.attachmentUnavailable'),
        deleted: t('toast:messages.attachmentDeleted'),
      }}
    />
  );
}
